import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { PageProps } from "next";
import { useEffect, useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { parseQueryString } from "shared/lib/utils";

import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { pathname as newslettersPathname } from "scmp-app/pages/newsletters";
import type { previewNewslettersPreviewPageQuery } from "scmp-app/queries/__generated__/previewNewslettersPreviewPageQuery.graphql";

import { NewsletterPreviewContextProvider } from "./contexts";
import IconArrow from "./icon-arrow.svg";
import {
  Container,
  DetailsHeader,
  Preview,
  StyledAppFooter,
  StyledMobileNewsletterPreview,
  StyledNewsletterCategorySlideshow,
  StyledNewsletterDetails,
  Wrapper,
} from "./styles";

const query = graphql`
  query previewNewslettersPreviewPageQuery($entityId: String) {
    newsletter(filter: { entityId: $entityId }) {
      sourceUrl
      newsletterCategories {
        newsletterCount: newsletters(first: 100) {
          edges {
            __typename
          }
        }
        ...newsletterCategoryItemSlideshowNewsletterCategory
      }
      ...mobileNewsletterPreviewNewsletter
      ...newsletterDetailsNewsletter
      ...newsletterItemNewsletter
      ...signUpButtonNewsletter
    }
  }
`;

const NewslettersPreviewPage: RelayComponent<unknown, previewNewslettersPreviewPageQuery> = ({
  preloadedQuery,
}) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const newsletterCategory = useMemo(
    () => first(data.newsletter.newsletterCategories ?? []),
    [data.newsletter.newsletterCategories],
  );

  const isSingleNewsletter = useMemo(
    () => (newsletterCategory?.newsletterCount?.edges?.length ?? 0) <= 1,
    [newsletterCategory?.newsletterCount?.edges?.length],
  );

  return (
    <NewsletterPreviewContextProvider ga4CustomParameter={{ triggerPoint: "newsletter preview" }}>
      <PageSeo
        additionalMetaTags={[]}
        openGraph={{ title: "Newsletters | South China Morning Post" }}
        title="Newsletters"
      />
      <Container>
        <Wrapper>
          <StyledNewsletterDetails
            isSingleNewsletter={isSingleNewsletter}
            reference={data.newsletter}
          >
            <DetailsHeader pathname={newslettersPathname}>
              <IconArrow />
              Newsletters & Email Alerts
            </DetailsHeader>
          </StyledNewsletterDetails>
          {data.newsletter.sourceUrl && <Preview sourceUrl={data.newsletter.sourceUrl} />}
          {newsletterCategory && !isSingleNewsletter && (
            <StyledNewsletterCategorySlideshow
              id="newsletterCategorySlideshow"
              reference={newsletterCategory}
            />
          )}
        </Wrapper>
        <StyledAppFooter />
      </Container>
      <StyledMobileNewsletterPreview reference={data.newsletter} />
    </NewsletterPreviewContextProvider>
  );
};

NewslettersPreviewPage.displayName = "NewslettersPreviewPage";

const pageProps: PageProps = {
  contentConfiguration: {
    responsivePadding: {
      tabletUp: 4,
    },
  },
};

export default withRelay(NewslettersPreviewPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    const entityId = parseQueryString(context.query.entityId) ?? "";
    return {
      entityId,
    };
  },
});
